import React from 'react';
import SEO from '../components/SEO';
import SolarCalculator from '../components/SolarCalculator';
import ProjectCarousel from '../components/ProjectCarousel';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Settings, Cog, TrendingUp } from 'lucide-react';

const Home = () => {
  const services = [
    {
      icon: <TrendingUp size={32} className="text-accent" />,
      title: 'Financing',
      description: 'Flexible financing solutions tailored to your business needs for solar power investments.'
    },
    {
      icon: <Settings size={32} className="text-accent" />,
      title: 'Designing',
      description: 'Expert design services optimized for maximum efficiency and energy generation.'
    },
    {
      icon: <Zap size={32} className="text-accent" />,
      title: 'Installation & Commissioning',
      description: 'Professional installation and commissioning of solar power plants with quality assurance.'
    },
    {
      icon: <Cog size={32} className="text-accent" />,
      title: 'O & M Services',
      description: 'Comprehensive operation and maintenance services to ensure optimal performance.'
    }
  ];

  const stats = [
    { value: '10+', label: 'MW Under OPEX Installation' },
    { value: '100+', label: 'CAPEX Projects' },
    { value: '50+', label: 'CAPEX Solar Plants' },
    { value: '15+', label: 'Years Experience of Expert Team' }
  ];

  return (
    <div>
      <SEO
        title="RESCO Invictus Green Energy Solutions - C&I Solar Power Plant Solutions"
        description="Leading provider of Commercial & Industrial Solar Power Plant solutions. Financing, Designing, Installing, Commissioning & O&M services."
        keywords="solar power, C&I solar, commercial solar, industrial solar, solar financing, solar installation, solar O&M"
        url="/"
      />

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/c8a7f66c-df10-498c-a57c-ebb1c482c942/images/c79433c1682b6629117d257cf56a6bbc9e6107bbea5e402944945a09965663d1.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-white/5 to-white/15"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32">
          <div className="max-w-3xl">
            {/* Watermark Logo */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden lg:block opacity-[0.06] pointer-events-none">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/invictus-green/uploads/974218eb-5249-49d9-9fa5-bef235aac409.png`}
                alt=""
                className="w-[1050px] h-[1050px] object-contain"
                aria-hidden="true"
              />
            </div>
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-4 block">C&I Solar Power Solutions</span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-primary mb-6" data-testid="hero-title">
              Powering Your Business with Clean Energy
            </h1>
            <p className="text-lg sm:text-xl text-text_muted leading-relaxed mb-8" data-testid="hero-description">
              Comprehensive C&I Solar Power Plant solutions from financing to operation. Transform your energy infrastructure with sustainable, cost-effective solar power.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all hover:-translate-y-1 shadow-lg"
                data-testid="hero-cta-contact"
              >
                Get Started
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white rounded font-semibold hover:bg-opacity-90 transition-all hover:-translate-y-1 shadow-lg"
                data-testid="hero-cta-projects"
              >
                View Projects
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-surface" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center" data-testid={`stat-${stat.label}`}>
                <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-text_muted font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Gallery Carousel */}
      <ProjectCarousel />

      {/* Services Section */}
      <section className="py-20 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-4 block" data-testid="services-overline">
              Our Services
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-semibold tracking-tight text-primary mb-4" data-testid="services-title">
              Complete Solar Solutions for Your Business
            </h2>
            <p className="text-base leading-relaxed text-text_muted max-w-3xl mx-auto" data-testid="services-description">
              From initial consultation to ongoing maintenance, we provide end-to-end solar power solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service) => (
              <div
                key={service.title}
                className="bg-surface p-8 rounded-sm border border-border hover:shadow-lg hover:-translate-y-1 transition-all"
                data-testid={`service-card-${service.title}`}
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-primary mb-3">{service.title}</h3>
                <p className="text-text_muted leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solar Savings Calculator */}
      <SolarCalculator />

      {/* CTA Section */}
      <section
        className="py-20 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/c8a7f66c-df10-498c-a57c-ebb1c482c942/images/4d8da598bb87b80a8f69dddfbd5e757daf08a6ba6e61ea4ac7463310b4887b61.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-primary/90"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-semibold tracking-tight text-white mb-6" data-testid="cta-title">
            Ready to Make the Switch to Solar?
          </h2>
          <p className="text-lg text-white/90 leading-relaxed mb-8 max-w-2xl mx-auto" data-testid="cta-description">
            Contact us today to discuss how we can help your business achieve energy independence and sustainability.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all hover:-translate-y-1 shadow-lg"
            data-testid="cta-button"
          >
            Contact Us Today
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
